import type { AppAbility, PermissionTuple } from '~/utils/ability'

/**
 * Cœur pur de la décision d'autorisation côté BO : le vocabulaire des exigences et leur résolution
 * contre l'ability. Aucun import React, MUI ni registre de routes, pour que la règle soit testable
 * et réutilisable (`utils/rbac.tsx` la réexporte, tous les appelants historiques passent par lui).
 */

/** `staticData.permission` accepte un tuple unique ou un tableau de tuples (tous requis). */
export type RoutePermission = PermissionTuple | PermissionTuple[]

/**
 * Exigence de permission d'une affordance : un tuple, un tableau de tuples (ET), ou `{ any: [...] }`
 * (OU — pour un bouton qui reste actionnable avec l'un OU l'autre droit, typiquement « Modifier »
 * qui devient « Voir » en lecture seule). Même vocabulaire que `RbacRequirement` côté backend.
 *
 * Les alternatives d'un `{ any: [...] }` sont elles-mêmes des exigences : un index de module peut
 * ainsi reprendre telles quelles les exigences de ses écrans, ET et OU compris, sans les aplatir
 * (ce qui élargirait l'accès — cf. `moduleIndexPermission`).
 */
export type PermissionRequirement = RoutePermission | { any: PermissionRequirement[] }

/**
 * Normalise en liste de tuples. Un tuple unique a son premier élément = action (string) ; un tableau
 * de tuples a son premier élément = tuple (array) — d'où la discrimination sur `Array.isArray`.
 */
export const normalizePermissions = (permission: RoutePermission): PermissionTuple[] =>
  Array.isArray(permission[0]) ? (permission as PermissionTuple[]) : [permission as PermissionTuple]

const isAnyRequirement = (
  permission: PermissionRequirement,
): permission is { any: PermissionRequirement[] } =>
  !Array.isArray(permission) && 'any' in permission

/**
 * Tuples manquants pour une exigence donnée. Tableau vide = autorisé.
 *
 * Fonction pure : socle commun des hooks (composants) ET de `withRoutePermissions`, qui s'exécute
 * dans un `beforeLoad` où aucun hook n'est disponible.
 */
export const resolveMissingPermissions = (
  ability: AppAbility,
  permission?: PermissionRequirement | null,
): PermissionTuple[] => {
  if (!permission) return []

  if (isAnyRequirement(permission)) {
    // Sémantique OU : si aucune alternative n'est satisfaite, on liste tous les tuples qui leur
    // manquent, pour que le tooltip nomme chacune des voies d'accès possibles.
    const missingPerAlternative = permission.any.map((alternative) =>
      resolveMissingPermissions(ability, alternative),
    )
    return missingPerAlternative.some((missing) => missing.length === 0)
      ? []
      : missingPerAlternative.flat()
  }

  return normalizePermissions(permission).filter((p) => ability.cannot(...p))
}
